/**
 * @file: inventory.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import {
  DeliveryCheckTemplate,
  InspectionSiteInfo,
  InspectionTemplate,
  OrderTag,
  PredefinedConstructionWorkItemCategory,
} from '@euler/model';
import {
  MaintenanceServiceSubject,
  VehicleInspectionSite,
  VehicleInspectionSiteCategory,
  VehicleSystemModule,
} from '@euler/model/entity';
import {
  InspectionTemplatePredefinedType,
  InspectionTemplateSceneType,
  ResourceAccessScope,
} from '@euler/model/enum';
import { QuotationTemplateConfig } from '@euler/model/quotation';

export type PredefinedConstructionWorkItemsResponse = {
  version: string;
  categories: PredefinedConstructionWorkItemCategory[];
};

export class InventoryService {
  constructor(private readonly api: HttpClient) {}

  async getVersions(): Promise<{ system: number; org: number; store: number }> {
    return await this.api.get().url('/inventory/versions').future();
  }

  async getCategories(): Promise<VehicleInspectionSiteCategory[]> {
    return await this.api.get().url('/inventory/categories').future();
  }

  async load(): Promise<{
    sites: InspectionSiteInfo[];
    versionInfo: { system: number; org: number; store: number };
  }> {
    return await this.api.get().url('/inventory').future();
  }

  async getSites(
    scope?: ResourceAccessScope,
  ): Promise<VehicleInspectionSite[]> {
    return await this.api.get().url('/inventory/sites', { scope }).future();
  }

  async getTemplates(params: {
    scope?: ResourceAccessScope;
    scene?: InspectionTemplateSceneType;
    type?: InspectionTemplatePredefinedType;
  }): Promise<InspectionTemplate[]> {
    return await this.api.get().url('/inventory/templates', params).future();
  }

  async getTemplateDetail(id: number): Promise<InspectionTemplate> {
    return await this.api
      .get()
      .url('/inventory/templates/:id', { id })
      .future();
  }

  async getDeliveryCheckTemplates(
    scope?: ResourceAccessScope,
  ): Promise<DeliveryCheckTemplate[]> {
    return await this.api
      .get()
      .url('/inventory/delivery-check-templates', {
        scope,
      })
      .future();
  }

  async getPredefinedConstructionWorkItems(): Promise<PredefinedConstructionWorkItemsResponse> {
    return await this.api
      .get()
      .url('/inventory/predefined-work-items')
      .future();
  }

  async getVehicleSystemModules(): Promise<VehicleSystemModule[]> {
    return await this.api
      .get()
      .url('/inventory/vehicle-system-modules')
      .future();
  }

  async getPredefinedOrderTags(): Promise<OrderTag[]> {
    return await this.api
      .get()
      .url('/inventory/predefined-order-tags')
      .future();
  }

  async getMaintenanceServiceSubjectInfo(
    name: string,
  ): Promise<MaintenanceServiceSubject | undefined> {
    return await this.api
      .get()
      .url('/inventory/maintenance-service-subjects/:name', { name })
      .future();
  }

  async getQuotationTemplateConfig(
    scope?: ResourceAccessScope,
  ): Promise<QuotationTemplateConfig> {
    return await this.api
      .get()
      .url('/inventory/quotation-template-config', { scope })
      .future();
  }
}
